import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Search, Filter, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import CategoryBar from "@/components/CategoryBar";
import { useRates } from "@/context/RatesContext";
import { useRecentlyViewed } from "@/context/RecentlyViewedContext";
import { Switch } from "@/components/ui/switch";
import { fmtINR } from "@/lib/pricing";
import { matchesSlug } from "@/lib/categories";

// Module-level constants — stable references prevent unnecessary Select re-renders.
const KARAT_OPTIONS = [
  { v: "All", label: "Karat" },
  { v: "24kt", label: "24kt" },
  { v: "22kt", label: "22kt" },
  { v: "18kt", label: "18kt" },
  { v: "14kt", label: "14kt" },
];
const DIAMOND_OPTIONS = [
  { v: "", label: "All Types" },
  { v: "true", label: "With Diamonds" },
  { v: "false", label: "Plain Gold" },
];
const SORT_OPTIONS = [
  { v: "", label: "Sort" },
  { v: "price_asc", label: "Price: Low → High" },
  { v: "price_desc", label: "Price: High → Low" },
  { v: "weight_asc", label: "Weight: Low → High" },
  { v: "weight_desc", label: "Weight: High → Low" },
  { v: "newest", label: "Newest first" },
];

export default function Catalogue() {
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { rates, gstOn, setGstOn } = useRates();
  const recent = useRecentlyViewed();

  // Ringstudio route forces collection filter
  const forcedCollection = loc.pathname === "/ringstudio" ? "ringstudio" : null;

  const q = params.get("q") || "";
  const category = params.get("category") || "All";
  const catSlug = params.get("cat") || null; // friendly taxonomy slug
  const karat = params.get("karat") || "All";
  const has_diamond = params.get("has_diamond");
  const collection = forcedCollection || params.get("collection") || "All";
  const sort = params.get("sort") || "";
  const minPrice = Number(params.get("min_price") || 0);
  const maxPrice = Number(params.get("max_price") || 0);

  // Memoised options arrays — depend only on the categories fetched once.
  const categoryOptions = useMemo(
    () => [{ v: "All", label: "Category" }, ...categories.map((c) => ({ v: c, label: c }))],
    [categories]
  );

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v === null || v === undefined || v === "" || v === "All") p.delete(k);
    else p.set(k, v);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    api.get("/items/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const run = setTimeout(async () => {
      try {
        const { data } = await api.get("/items", {
          params: {
            q: q || undefined,
            category: category !== "All" ? category : undefined,
            karat: karat !== "All" ? karat : undefined,
            has_diamond: has_diamond === "true" ? true : has_diamond === "false" ? false : undefined,
            collection: collection !== "All" ? collection : undefined,
            sort: sort || undefined,
            limit: 80,
          },
        });
        setItems(data.items);
        setTotal(data.total);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(run);
  }, [q, category, karat, has_diamond, collection, sort]);

  // Price range + friendly category filter (client-side)
  const displayItems = useMemo(() => {
    let list = items;
    if (catSlug) list = list.filter((it) => matchesSlug(it, catSlug));
    if (!minPrice && !maxPrice) return list;
    return list.filter((it) => {
      const p = it.mrp || (it.gold_weight && rates ? it.gold_weight * (rates.rate_22kt || 0) : 0);
      if (minPrice && p < minPrice) return false;
      if (maxPrice && p > maxPrice) return false;
      return true;
    });
  }, [items, catSlug, minPrice, maxPrice, rates]);

  useEffect(() => {
    const tag = params.get("tag");
    if (tag && items.length) {
      const it = items.find((i) => i.tag_no.toLowerCase() === tag.toLowerCase());
      if (it) setSelected(it);
    }
  }, [params, items]);

  const onOpen = (it) => { setSelected(it); recent?.push?.(it); };

  return (
    <div className="bg-white">
      {/* Page header — ivory surface, mirrors /solitaires */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-6">
        <div className="ornament mb-3" style={{ color: "#A88934" }}>{forcedCollection === "ringstudio" ? "Ringstudio Collection" : "The Collection"}</div>
        <h1 className="font-display leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#0B1A33", fontWeight: 400 }}>
          {forcedCollection === "ringstudio" ? "Ringstudio" : "Catalogue"} <em style={{ fontStyle: "italic", color: "#A88934" }}>— {total} pieces.</em>
        </h1>
        {rates && (
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]" style={{ color: "rgba(11,26,51,0.65)" }}>
            <RateChip label="22kt" value={rates.rate_22kt} />
            <RateChip label="18kt" value={rates.rate_18kt} />
            <RateChip label="14kt" value={rates.rate_14kt} />
            <RateChip label="Diamond" value={`${fmtINR(rates.rate_diamond)}/ct`} raw />
          </div>
        )}
      </section>

      {/* Visible category bar (taxonomy-based) — only on /catalogue, not on /ringstudio */}
      {!forcedCollection && <CategoryBar variant="catalogue" />}

      {/* Collection pills */}
      {!forcedCollection && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-3">
          <div className="flex gap-2 flex-wrap" data-testid="collection-pills">
            <CollectionPill active={collection === "All"} onClick={() => setParam("collection", null)}>All</CollectionPill>
            <CollectionPill active={collection === "main"} onClick={() => setParam("collection", "main")}>Heritage</CollectionPill>
            <button
              onClick={() => setParam("collection", "ringstudio")}
              className={`px-4 py-2 text-xs tracking-[0.25em] uppercase border flex items-center gap-2 transition-colors ${collection === "ringstudio" ? "bg-navy-900 text-ivory" : "bg-white hover:bg-gold/10"}`}
              style={collection === "ringstudio" ? { background: "#0B1A33", color: "#FAF6EE", borderColor: "#0B1A33" } : { color: "#A88934", borderColor: "rgba(168,137,52,0.5)" }}
              data-testid="pill-ringstudio"
            >
              <Sparkles className="w-3 h-3" /> Ringstudio
            </button>
          </div>
        </section>
      )}

      {/* Filter bar — flat ivory surface, mirrors /solitaires; NOT sticky so it never overlaps cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-4">
        <div className="flex flex-wrap items-center gap-3 p-4 border" style={{ borderColor: "rgba(201,168,76,0.25)", background: "#FAF7EE" }}>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(11,26,51,0.5)" }} />
            <input
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search by tag, name or weight…"
              data-testid="catalogue-search"
              className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border focus:outline-none"
              style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33" }}
            />
          </div>
          <Select value={category} onChange={(v) => setParam("category", v)} testId="filter-category" options={categoryOptions} />
          <Select value={karat} onChange={(v) => setParam("karat", v)} testId="filter-karat" options={KARAT_OPTIONS} />
          <Select value={has_diamond ?? ""} onChange={(v) => setParam("has_diamond", v || null)} testId="filter-diamond" options={DIAMOND_OPTIONS} />
          <Select value={sort} onChange={(v) => setParam("sort", v || null)} testId="filter-sort" options={SORT_OPTIONS} />
          <label className="flex items-center gap-2 px-3 py-2.5 text-sm bg-white border cursor-pointer" style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33" }}>
            <Switch checked={gstOn} onCheckedChange={setGstOn} data-testid="gst-toggle-catalogue" />
            <span className="text-xs tracking-widest uppercase">GST 3%</span>
          </label>
          {(q || category !== "All" || karat !== "All" || has_diamond || sort || minPrice || maxPrice) && (
            <button onClick={() => setParams(forcedCollection ? { collection: "ringstudio" } : {})} className="text-[11px] tracking-widest uppercase underline" style={{ color: "#A88934" }} data-testid="clear-filters">
              <X className="w-3 h-3 inline" /> Reset
            </button>
          )}
        </div>

        {/* Price range — subtle row beneath the main filter bar */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" style={{ color: "rgba(11,26,51,0.65)" }}>
          <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "rgba(11,26,51,0.55)" }}>Price</span>
          <input type="number" placeholder="Min ₹" value={minPrice || ""} onChange={(e) => setParam("min_price", e.target.value || null)} className="w-28 px-3 py-2 text-sm bg-white border focus:outline-none" style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33" }} data-testid="min-price" />
          <span style={{ color: "rgba(11,26,51,0.4)" }}>—</span>
          <input type="number" placeholder="Max ₹" value={maxPrice || ""} onChange={(e) => setParam("max_price", e.target.value || null)} className="w-28 px-3 py-2 text-sm bg-white border focus:outline-none" style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33" }} data-testid="max-price" />
          <span className="text-[11px] tracking-[0.25em] uppercase ml-auto" style={{ color: "rgba(11,26,51,0.55)" }}>{displayItems.length} of {total} visible</span>
        </div>
      </section>

      {/* Grid — mirrors Solitaires exactly (2 / 3 / 4 columns, gap-5) */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="catalogue-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skel-${i}`} className="aspect-[4/5] border animate-pulse" style={{ background: "#F5F0E2", borderColor: "rgba(201,168,76,0.2)" }} />
            ))}
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-20" style={{ color: "rgba(11,26,51,0.6)" }}>
            <Filter className="w-10 h-10 mx-auto mb-4" style={{ color: "#C9A84C" }} strokeWidth={0.8} />
            <p className="text-lg font-display">No pieces match these filters.</p>
            <p className="text-sm mt-2">Try resetting filters or broadening the search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="catalogue-grid">
            {displayItems.map((it, i) => (
              <ProductCard key={it.id || it.tag_no} item={it} index={i} onOpen={onOpen} />
            ))}
          </div>
        )}
      </section>

      {/* Recently viewed */}
      {recent?.items?.length > 1 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 border-t pt-10" style={{ borderColor: "rgba(201,168,76,0.25)" }} data-testid="recently-viewed">
          <div className="ornament mb-6" style={{ color: "#A88934" }}>Recently Viewed</div>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {recent.items.map((it) => (
              <button
                key={it.tag_no}
                onClick={() => onOpen(it)}
                className="flex-shrink-0 w-40 group text-left"
                data-testid={`recent-${it.tag_no}`}
              >
                <div className="aspect-[4/5] border overflow-hidden" style={{ borderColor: "rgba(201,168,76,0.25)", background: "#FAF7EE" }}>
                  {it.image_path ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.image_path}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "rgba(168,137,52,0.5)" }}>—</div>
                  )}
                </div>
                <div className="mt-2">
                  <div className="overline" style={{ color: "#A88934" }}>{it.tag_no}</div>
                  <div className="text-xs truncate" style={{ color: "rgba(11,26,51,0.75)" }}>{it.item_name}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {selected && <ProductModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const RateChip = ({ label, value, raw }) => (
  <div className="px-3 py-1 border" style={{ borderColor: "rgba(201,168,76,0.4)", background: "#FAF7EE" }}>
    <span className="mr-2 text-[10px] uppercase tracking-widest" style={{ color: "#A88934" }}>{label}</span>
    <span style={{ color: "#0B1A33" }}>{raw ? value : `${fmtINR(value)}/g`}</span>
  </div>
);

const CollectionPill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className="px-4 py-2 text-xs tracking-[0.25em] uppercase border transition-colors"
    style={active
      ? { background: "#0B1A33", color: "#FAF6EE", borderColor: "#0B1A33" }
      : { background: "#FFFFFF", color: "#0B1A33", borderColor: "rgba(11,26,51,0.2)" }}
  >
    {children}
  </button>
);

const Select = ({ value, onChange, testId, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="px-3 py-2.5 text-sm bg-white border focus:outline-none cursor-pointer"
    style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33", minWidth: 120 }}
  >
    {options.map((o) => (<option key={o.v || "_blank"} value={o.v}>{o.label}</option>))}
  </select>
);
